import axiosInstance from '../utils/axiosConfig';
import { invoiceScreenPath } from '../utils/invoiceScreenPath';

/**
 * Bunker Invoice Approvals (ClickUp 2026-09-17, "Deliveries versus invoices").
 *
 * Invoices raised on a bunker delivery are saved as drafts; the Bunker
 * Operations Approvals group checks them on one screen and is the only group
 * that sends them to finance. The RULE lives on the server
 * (backend/invoicing/bunker_invoice_approvals.py). These two calls only ask it,
 * so no screen can hold a second copy that drifts.
 */

export const APPROVALS_SCREEN_PATH = '/invoicing/bunker-approvals';

/**
 * The invoice's own screen, opened WITH its Invoice Papers beside it, and the
 * way back to this list.
 *
 * ONE home for the link the approvals list follows (the same home as on HMS,
 * where both looks share it). The approver is here to check the
 * invoice against the delivery's papers, so the screen opens on the split
 * (ClickUp 2026-10-23, "double screen ... where there are approvals") instead
 * of making them find the Invoice Papers button first.
 */
export const invoicePath = (invoice) => invoiceScreenPath(
  // The row's own kind ('incoming' or 'outgoing' today); a kind with no
  // screen answers null and the list shows the name without a link.
  invoice.kind,
  invoice.id,
  { withPapers: true, returnTo: APPROVALS_SCREEN_PATH },
);

/**
 * The approvals list: one row per bunker delivery with both invoice columns.
 *
 * Returns every row plus the server's own totals (`count`, `total_deliveries`,
 * `waiting_invoices`) so the screen can say how much it is showing. A failure
 * is thrown, never turned into an empty list: "could not load" and "nothing
 * waiting" must never look alike.
 *
 * @param {'waiting'|'all'} view
 */
export const fetchBunkerInvoiceApprovals = async (view = 'waiting') => {
  const response = await axiosInstance.get('/api/invoicing/bunker-invoice-approvals/', {
    params: { view },
  });
  return response.data;
};

/**
 * May Save & Workflow be offered on this invoice screen?
 *
 * The server answers with the same function the save itself enforces.
 *
 * @param {object} args
 * @param {'outgoing'|'incoming'} args.document
 * @param {number|string|null} [args.id] - a saved invoice
 * @param {number|string|null} [args.deliveryId] - the delivery a new invoice is raised from
 * @returns {Promise<{allowed: boolean, reason: string|null, is_bunker_invoice: boolean}>}
 */
export const fetchSendToFinance = async ({ document, id = null, deliveryId = null }) => {
  const params = { document };
  if (id !== null && id !== undefined && id !== '') params.id = id;
  if (deliveryId !== null && deliveryId !== undefined && deliveryId !== '') params.delivery = deliveryId;
  const response = await axiosInstance.get(
    '/api/invoicing/bunker-invoice-approvals/send-to-finance/',
    { params },
  );
  return response.data;
};

const bunkerInvoiceApprovalsService = {
  fetchBunkerInvoiceApprovals,
  fetchSendToFinance,
};

export default bunkerInvoiceApprovalsService;
